import { IHttpApi } from '@cian/http-api';
import { WebSocketClient, IWSServerErrorSchema } from '@cian/chats-api/lib/browser-entry';
import { ILogger } from '@cian/logger/shared';
import { ValidationError } from '@cian/peperrors/shared';
import { fetchUsersSession, TUsersSessionResponse } from '../../../../repositories/chats/v1/users-session';
import { Registration } from './registration';
import { delay } from '../../../../utils/delay';

// Не использовать при SSR. Если нужен SSR, то необходимо переделать на scopes из DI,
// чтобы для каждого пользователя (запроса) создавалась своя сессия.
let session: Session | null = null;

export class Session {
  public sessionToken: string | null = null;
  public userId: number | null = null;
  public isExpired = true;
  public isRegistered = false;

  private worker: Promise<void> | null = null;
  private closeHandlerId: string;
  private messageHandlerId: string;

  public static createSession(socket: WebSocketClient, httpApi: IHttpApi, logger: ILogger) {
    if (session) {
      return session;
    }

    session = new Session(socket, httpApi, logger);

    return session;
  }

  public static removeSession() {
    /* istanbul ignore next */
    // eslint-disable-next-line no-unused-expressions
    session?.destroy();

    session = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-parameter-properties
  public constructor(private socket: WebSocketClient, private httpApi: IHttpApi, private logger: ILogger) {
    this.closeHandlerId = this.socket.addEventListener('close', this.onClose);
    this.messageHandlerId = this.socket.addEventListener('message', this.onMessage);
  }

  public async update() {
    if (this.worker) {
      return this.worker;
    }

    try {
      this.worker = this.work();

      await this.worker;

      this.worker = null;
    } catch (error) {
      this.logger.debug(error);

      this.worker = null;
      this.socket.close();
    }
  }

  public isReady() {
    return !this.isExpired && this.isRegistered && !this.worker;
  }

  public destroy() {
    this.socket.removeEventListener('close', this.closeHandlerId);
    this.socket.removeEventListener('message', this.messageHandlerId);
    this.isExpired = true;
    this.isRegistered = false;
    this.worker = null;
  }

  private async work() {
    if (this.isExpired) {
      await this.getToken();

      this.isExpired = false;
    }

    if (!this.isRegistered) {
      await this.register();

      this.isRegistered = true;
    }
  }

  private async register() {
    const registration = new Registration(this.socket, this.sessionToken as string, this.userId as number);

    await registration.register();
  }

  private async getToken() {
    try {
      const result: TUsersSessionResponse = await fetchUsersSession({ httpApi: this.httpApi, parameters: {} });

      if (result.statusCode === 200) {
        this.sessionToken = result.response.sessionToken as string;
        this.userId = result.response.userId as number;
      } else {
        this.sessionToken = null;

        throw new ValidationError({
          message: "Couldn't update session token",
          domain: 'chats#session#getToken()',
        });
      }
    } catch (error) {
      this.logger.debug(error);
      this.sessionToken = null;

      await delay(10000);

      throw error;
    }
  }

  private onMessage = (socketEvent: MessageEvent) => {
    const event: IWSServerErrorSchema = JSON.parse(socketEvent.data);

    /* istanbul ignore next */
    if (event.type === 'error') {
      const {
        error: { message },
      } = event;

      /* istanbul ignore next */
      if (message === 'invalidSession' || message === 'sessionExpired') {
        this.isExpired = true;
        this.isRegistered = false;

        this.update();
      }
    }
  };

  private onClose = () => {
    this.isRegistered = false;
  };
}
